import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CampaignService, FaqService, FaqTopicService } from '../../../_services';
import {
  buildFaqTree, subtreeFaqIds, topicCheckState, leafSelectedCount,
  parentSelectedSubtopicCount, findTopic, isLeaf, FaqTreeTopic,
} from './omp-faq-tree.util';

@Component({
  selector: 'omp-faq-tree',
  templateUrl: './omp-faq-tree.component.html',
  styleUrls: ['./omp-controls.scss'],
})
export class OmpFaqTreeComponent implements OnInit, OnDestroy {
  @Input() value: number[];
  @Output() valueChange = new EventEmitter<number[]>();

  public tree: FaqTreeTopic[] = [];
  public selected = new Set<number>();
  public expanded = new Set<number>();
  public loading = true;

  private destroyed$ = new Subject<void>();

  constructor(
    private faqService: FaqService,
    private faqTopicService: FaqTopicService,
    private campaignService: CampaignService,
  ) {}

  ngOnInit() {
    this.selected = new Set<number>(this.value ? this.value : []);
    forkJoin([this.faqTopicService.list(), this.faqService.list()]).pipe(takeUntil(this.destroyed$)).subscribe(
      ([topics, faqs]: [any[], any[]]) => {
        const topicRows = (topics ? topics : []).map((t) => ({ id: +t.id, title: t.title, parent: t.parent }));
        const faqRows = (faqs ? faqs : [])
          .filter((f) => f.display)
          .map((f) => ({ id: +f.id, question: f.question, topics: f.topics }));
        const roots = buildFaqTree(topicRows, faqRows);
        this.tree = this.scopeToCampaignTopic(roots);
        this.loading = false;
      },
      () => {
        this.tree = [];
        this.loading = false;
      }
    );
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  // When the campaign pins a faq_topic, root the tree at that topic's children.
  private scopeToCampaignTopic(roots: FaqTreeTopic[]): FaqTreeTopic[] {
    const campaign = this.campaignService.getCurrentCampaign();
    let scoped = roots;
    if (campaign && campaign.faq_topic) {
      const topic = findTopic(roots, +campaign.faq_topic);
      scoped = topic ? topic.children : [];
    }
    return scoped;
  }

  checkState(node: FaqTreeTopic): string {
    return topicCheckState(node, this.selected);
  }

  isLeafNode(node: FaqTreeTopic): boolean {
    return isLeaf(node);
  }

  leafSelected(node: FaqTreeTopic): number {
    return leafSelectedCount(node, this.selected);
  }

  parentSelectedCount(node: FaqTreeTopic): number {
    return parentSelectedSubtopicCount(node, this.selected);
  }

  toggleExpand(node: FaqTreeTopic) {
    if (this.expanded.has(node.id)) {
      this.expanded.delete(node.id);
    } else {
      this.expanded.add(node.id);
    }
  }

  toggleTopic(node: FaqTreeTopic) {
    const ids = subtreeFaqIds(node);
    const allSelected = ids.every((id) => this.selected.has(id));
    ids.forEach((id) => {
      if (allSelected) {
        this.selected.delete(id);
      } else {
        this.selected.add(id);
      }
    });
    this.emit();
  }

  toggleFaq(faqId: number) {
    if (this.selected.has(faqId)) {
      this.selected.delete(faqId);
    } else {
      this.selected.add(faqId);
    }
    this.emit();
  }

  private emit() {
    this.valueChange.emit(Array.from(this.selected));
  }
}
